import { API_BASE } from "./config/api";
import React, { useEffect, useState } from "react";
import { Modal, Button, Alert, Descriptions, Typography, Select, Space } from "antd";
import axios from "axios";

const { Text } = Typography;


export default function RtaPasswordsModal({ open, onClose }) {
  const [arns, setArns] = useState([]);
  const [selectedArn, setSelectedArn] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const authHeaders = () => {
    const token = sessionStorage.getItem("distributor_auth_token") || "";
    return { "X-KG-Distributor-Authorization": `Bearer ${token}` };
  };

  const loadArnList = async () => {
    setError("");
    setData(null);
    setSelectedArn("");
    setListLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin_rta_passwords.php`, {
        headers: authHeaders()
      });
      if (res?.data?.status !== "ok" || !Array.isArray(res?.data?.arns)) {
        throw new Error("Invalid ARN list response.");
      }
      setArns(res.data.arns);
    } catch (e) {
      setArns([]);
      setError(e?.response?.data?.message || e?.message || "Unable to load ARN list.");
    } finally {
      setListLoading(false);
    }
  };

  const loadCredentials = async (arn) => {
    setSelectedArn(arn || "");
    setData(null);
    setError("");
    if (!arn) return;

    setCredentialLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin_rta_passwords.php`, {
        params: { arn },
        headers: authHeaders()
      });
      if (res?.data?.status !== "ok") {
        throw new Error(res?.data?.message || "Unable to load RTA credentials.");
      }
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Unable to load RTA credentials.");
    } finally {
      setCredentialLoading(false);
    }
  };

  const close = () => {
    setArns([]);
    setSelectedArn("");
    setData(null);
    setError("");
    setListLoading(false);
    setCredentialLoading(false);
    onClose?.();
  };

  useEffect(() => {
    if (open) loadArnList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = arns.map((item) => ({
    value: item.arn_number,
    label: item.arn_holder_name
      ? `${item.arn_number} — ${item.arn_holder_name}`
      : item.arn_number
  }));

  return (
    <Modal
      title="RTA File Credentials"
      open={open}
      onCancel={close}
      footer={<Button onClick={close}>Close</Button>}
      destroyOnClose
      maskClosable={false}
      width={650}
    >
      <Alert
        type="warning"
        showIcon
        message="Admin-only confidential information"
        description="Select an ARN to view its CAMS and KFin file credentials."
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
        <Text strong>Select ARN</Text>
        <Select
          showSearch
          allowClear
          value={selectedArn || undefined}
          placeholder={listLoading ? "Loading ARNs..." : "Select ARN"}
          loading={listLoading}
          disabled={listLoading}
          options={options}
          onChange={loadCredentials}
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label || "").toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: "100%" }}
        />
      </Space>

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      )}

      {credentialLoading && <Text>Loading secure credentials...</Text>}

      {!credentialLoading && data && (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="ARN">{data.arn_number || "—"}</Descriptions.Item>
          <Descriptions.Item label="ARN Holder">{data.arn_holder_name || "—"}</Descriptions.Item>
          <Descriptions.Item label="CAMS ID">{data.cams_id || "—"}</Descriptions.Item>
          <Descriptions.Item label="CAMS Password">
            <Text copyable>{data.cams_password || "—"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="KFin ID">{data.kfin_id || "—"}</Descriptions.Item>
          <Descriptions.Item label="KFin Password">
            <Text copyable>{data.kfin_password || "—"}</Text>
          </Descriptions.Item>
        </Descriptions>
      )}

      {!listLoading && !credentialLoading && !data && !error && (
        <Text type="secondary">Choose an ARN from the list above.</Text>
      )}
    </Modal>
  );
}
